import { ref, computed, onBeforeUnmount } from 'vue';
import {
  cellToRowCol,
  rowColToCell,
  canPlace,
  findSwapTarget,
  resizeTo,
} from './widgetGrid';

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

// Mover e redimensionar com eventos de ponteiro sobre a grade (sem biblioteca).
// `onCommit(positions)` recebe o layout a salvar; `onReject()` avisa que o destino não cabe.
export const useWidgetLayoutDrag = ({
  gridRef,
  widgets,
  columns,
  rows,
  onCommit,
  onReject,
}) => {
  const drag = ref(null);

  const pointerCell = event => {
    const box = gridRef.value.getBoundingClientRect();
    return {
      col: Math.floor(((event.clientX - box.left) / box.width) * columns.value),
      row: Math.floor(((event.clientY - box.top) / box.height) * rows.value),
    };
  };

  const othersOf = widget =>
    widgets.value.filter(item => item.id !== widget.id);

  const movePreview = (event, { widget, offset }) => {
    const cell = pointerCell(event);
    const rawRow = cell.row - offset.row;
    const rawCol = cell.col - offset.col;
    const row = clamp(rawRow, 0, rows.value - widget.height);
    const col = clamp(rawCol, 0, columns.value - widget.width);
    const rect = { ...widget, position: rowColToCell(row, col, columns.value) };
    const inside = row === rawRow && col === rawCol;
    const others = othersOf(widget);
    const fits = inside && canPlace(rect, others, columns.value, rows.value);
    const swapWith =
      inside && !fits
        ? findSwapTarget(rect, rect.position, others, columns.value)
        : undefined;
    if (swapWith) return { rect: swapWith, valid: true, inside, swapWith };
    return { rect, valid: fits, inside };
  };

  const resizePreview = (event, { widget, direction }) => {
    const cell = pointerCell(event);
    const origin = cellToRowCol(widget.position, columns.value);
    const size = resizeTo(
      widget,
      direction,
      { width: cell.col - origin.col + 1, height: cell.row - origin.row + 1 },
      othersOf(widget),
      columns.value,
      rows.value
    );
    return { rect: { ...widget, ...size }, valid: true, inside: true };
  };

  const updatePreview = event => {
    const state = drag.value;
    state.preview =
      state.mode === 'move'
        ? movePreview(event, state)
        : resizePreview(event, state);
  };

  const rectPayload = ({ id, position, width, height }) => ({
    id,
    position,
    width,
    height,
  });

  const positionsToSave = ({ mode, widget, preview }) => {
    if (mode === 'resize') {
      const changed =
        preview.rect.width !== widget.width ||
        preview.rect.height !== widget.height;
      return changed ? [rectPayload(preview.rect)] : [];
    }
    if (preview.swapWith) {
      return [
        rectPayload({ ...widget, position: preview.swapWith.position }),
        rectPayload({ ...preview.swapWith, position: widget.position }),
      ];
    }
    if (!preview.valid) {
      if (preview.inside) onReject();
      return [];
    }
    return preview.rect.position === widget.position
      ? []
      : [rectPayload(preview.rect)];
  };

  const stop = () => {
    window.removeEventListener('pointermove', updatePreview);
    window.removeEventListener('pointerup', finish); // eslint-disable-line no-use-before-define
    window.removeEventListener('pointercancel', cancel); // eslint-disable-line no-use-before-define
  };

  function cancel() {
    stop();
    drag.value = null;
  }

  function finish(event) {
    updatePreview(event);
    const positions = positionsToSave(drag.value);
    cancel();
    if (positions.length) onCommit(positions);
  }

  const start = (event, state) => {
    event.preventDefault();
    drag.value = { ...state, preview: null };
    window.addEventListener('pointermove', updatePreview);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', cancel);
  };

  const startMove = (event, widget) => {
    const cell = pointerCell(event);
    const origin = cellToRowCol(widget.position, columns.value);
    start(event, {
      mode: 'move',
      widget,
      offset: {
        row: clamp(cell.row - origin.row, 0, widget.height - 1),
        col: clamp(cell.col - origin.col, 0, widget.width - 1),
      },
    });
  };

  // Só o botão principal (mouse esquerdo, toque ou caneta): o clique direito
  // abriria o menu do navegador e o pointerup não chegaria, deixando o arrasto preso.
  const startResize = (event, widget, direction) => {
    if (event.button > 0) return;
    start(event, { mode: 'resize', widget, direction });
  };

  onBeforeUnmount(cancel);

  return {
    preview: computed(() => drag.value?.preview ?? null),
    movingId: computed(() =>
      drag.value?.mode === 'move' ? drag.value.widget.id : null
    ),
    isDragging: computed(() => !!drag.value),
    startMove,
    startResize,
  };
};
